import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveDishGalleryImageUrl } from "../../utils/contentApi";
import "./dishGalleryCarousel.css";

function DishGalleryCarousel({
  items = [],
  heading = "Galerie",
  description = "",
  autoplay = true,
  className = "",
}) {
  const viewportRef = useRef(null);
  const cardRefs = useRef([]);
  const isPausedRef = useRef(false);
  const isPointerDownRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const dragStartIndexRef = useRef(0);
  const dragDeltaXRef = useRef(0);
  const didDragRef = useRef(false);
  const ignoreClickRef = useRef(false);
  const resetDragTimerRef = useRef(null);
  const scrollResetTimerRef = useRef(null);
  const autoplayTimerRef = useRef(null);
  const [zoomItem, setZoomItem] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const centerCard = (index, behavior = "smooth") => {
    const viewport = viewportRef.current;
    const card = cardRefs.current[index];
    if (!viewport || !card) {
      return;
    }

    const targetLeft =
      card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2;
    viewport.scrollTo({
      left: Math.max(0, targetLeft),
      behavior,
    });
  };
  const resolvedItems = useMemo(
    () =>
      Array.isArray(items)
        ? items
            .map((item) => ({
              ...item,
              imageUrl: resolveDishGalleryImageUrl(item?.imageUrl),
            }))
            .filter((item) => item.imageUrl)
        : [],
    [items],
  );

  useEffect(() => {
    isPausedRef.current = false;
    isPointerDownRef.current = false;
    didDragRef.current = false;
    ignoreClickRef.current = false;
    if (resetDragTimerRef.current) {
      window.clearTimeout(resetDragTimerRef.current);
      resetDragTimerRef.current = null;
    }

    if (scrollResetTimerRef.current) {
      window.clearTimeout(scrollResetTimerRef.current);
      scrollResetTimerRef.current = null;
    }

    if (autoplayTimerRef.current) {
      window.clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }

    const initialIndex = Math.floor(Math.max(0, resolvedItems.length - 1) / 2);
    setActiveIndex(initialIndex);
    cardRefs.current = [];

    window.requestAnimationFrame(() => {
      centerCard(initialIndex, "auto");
    });
  }, [resolvedItems]);

  const getClosestIndexToViewportCenter = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || resolvedItems.length === 0) {
      return 0;
    }

    const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    resolvedItems.forEach((_item, index) => {
      const card = cardRefs.current[index];
      if (!card) {
        return;
      }

      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    return bestIndex;
  }, [resolvedItems]);

  const updateActiveIndex = useCallback(() => {
    const bestIndex = getClosestIndexToViewportCenter();

    setActiveIndex((current) => (current === bestIndex ? current : bestIndex));
  }, [getClosestIndexToViewportCenter]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    let rafId = 0;
    const handleScroll = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateActiveIndex();
      });
    };

    viewport.addEventListener("scroll", handleScroll, { passive: true });
    updateActiveIndex();

    const initialIndex = Math.floor(Math.max(0, resolvedItems.length - 1) / 2);
    window.requestAnimationFrame(() => {
      centerCard(initialIndex, "auto");
    });

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [resolvedItems.length, updateActiveIndex]);

  useEffect(() => {
    if (!autoplay || resolvedItems.length < 2) {
      return undefined;
    }

    const scheduleNextSlide = () => {
      if (autoplayTimerRef.current) {
        window.clearTimeout(autoplayTimerRef.current);
      }

      autoplayTimerRef.current = window.setTimeout(() => {
        if (!isPausedRef.current && !isPointerDownRef.current) {
          const nextIndex = activeIndex >= resolvedItems.length - 1 ? 0 : activeIndex + 1;
          if (nextIndex === 0 && activeIndex === resolvedItems.length - 1) {
            centerCard(0, "auto");
            setActiveIndex(0);
          } else {
            centerCard(nextIndex, "smooth");
            setActiveIndex(nextIndex);
          }
        }

        scheduleNextSlide();
      }, 3800);
    };

    scheduleNextSlide();

    return () => {
      if (autoplayTimerRef.current) {
        window.clearTimeout(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [autoplay, resolvedItems.length, activeIndex]);

  useEffect(() => {
    if (!zoomItem) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setZoomItem(null);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [zoomItem]);

  const pause = () => {
    isPausedRef.current = true;
  };

  const resume = () => {
    if (!isPointerDownRef.current) {
      isPausedRef.current = false;
    }
  };

  const handlePointerDown = (event) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    isPointerDownRef.current = true;
    setIsDragging(true);
    didDragRef.current = false;
    ignoreClickRef.current = false;
    isPausedRef.current = true;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = viewport.scrollLeft;
    dragStartIndexRef.current = activeIndex;
    dragDeltaXRef.current = 0;

    if (typeof viewport.setPointerCapture === "function") {
      viewport.setPointerCapture(event.pointerId);
    }
  };

  const handlePointerMove = (event) => {
    if (!isPointerDownRef.current) {
      return;
    }

    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const deltaX = event.clientX - dragStartXRef.current;
    dragDeltaXRef.current = deltaX;
    if (Math.abs(deltaX) > 6) {
      didDragRef.current = true;
      if (event.cancelable) {
        event.preventDefault();
      }
    }

    viewport.scrollLeft = dragStartScrollLeftRef.current - deltaX;
    updateActiveIndex();
  };

  const finishPointer = () => {
    if (!isPointerDownRef.current) {
      return;
    }

    isPointerDownRef.current = false;
    setIsDragging(false);
    ignoreClickRef.current = didDragRef.current;

    let targetIndex = getClosestIndexToViewportCenter();
    const startIndex = dragStartIndexRef.current;
    const startCard = cardRefs.current[startIndex];
    const dragDistance = dragDeltaXRef.current;

    if (didDragRef.current && startCard) {
      const switchThreshold = Math.max(38, startCard.offsetWidth * 0.1);
      if (Math.abs(dragDistance) >= switchThreshold) {
        const direction = dragDistance < 0 ? 1 : -1;
        targetIndex = Math.max(
          0,
          Math.min(resolvedItems.length - 1, startIndex + direction),
        );
      }
    }

    setActiveIndex(targetIndex);
    centerCard(targetIndex, "smooth");

    if (resetDragTimerRef.current) {
      window.clearTimeout(resetDragTimerRef.current);
    }

    resetDragTimerRef.current = window.setTimeout(() => {
      didDragRef.current = false;
      ignoreClickRef.current = false;
      isPausedRef.current = false;
    }, 180);
  };

  const scrollByCard = (direction) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    isPausedRef.current = true;
    const targetIndex = Math.max(
      0,
      Math.min(resolvedItems.length - 1, activeIndex + direction),
    );
    centerCard(targetIndex, "smooth");
    setActiveIndex(targetIndex);

    if (resetDragTimerRef.current) {
      window.clearTimeout(resetDragTimerRef.current);
    }

    resetDragTimerRef.current = window.setTimeout(() => {
      isPausedRef.current = false;
    }, 400);
  };

  const handleItemClick = (item, event) => {
    if (ignoreClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    setZoomItem(item);
  };

  if (resolvedItems.length === 0) {
    return null;
  }

  return (
    <section className={`dish-gallery-carousel-shell ${className}`.trim()} aria-label={heading}>
      <div className="dish-gallery-carousel-header">
        <div>
          <h2>{heading}</h2>
          {description && <p>{description}</p>}
        </div>
        <div className="dish-gallery-carousel-badge">{resolvedItems.length} photo{resolvedItems.length > 1 ? "s" : ""}</div>
      </div>

      <div className="dish-gallery-carousel-viewport-wrap">
        <button
          type="button"
          className="dish-gallery-carousel-nav dish-gallery-carousel-nav-prev"
          aria-label="Photo précédente"
          onClick={() => scrollByCard(-1)}
        >
          ‹
        </button>

        <div
          ref={viewportRef}
          className={`dish-gallery-carousel-viewport${isDragging ? " is-dragging" : ""}`}
          onMouseEnter={pause}
          onMouseLeave={resume}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishPointer}
          onPointerCancel={finishPointer}
          onLostPointerCapture={finishPointer}
        >
          <div className="dish-gallery-carousel-track">
            {resolvedItems.map((item, index) => (
              <button
                key={`${item.id || item.imageUrl}`}
                type="button"
                ref={(node) => {
                  cardRefs.current[index] = node;
                }}
                className={`dish-gallery-carousel-card${index === activeIndex ? " is-active" : ""}`}
                onClick={(event) => handleItemClick(item, event)}
              >
                <img
                  src={item.imageUrl}
                  alt={item.title || heading}
                  draggable={false}
                  loading={Math.abs(index - activeIndex) <= 1 ? "eager" : "lazy"}
                  decoding="async"
                />
                <span className="dish-gallery-carousel-caption">
                  {String(item.title || "Photo du plat").trim() || "Photo du plat"}
                </span>
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="dish-gallery-carousel-nav dish-gallery-carousel-nav-next"
          aria-label="Photo suivante"
          onClick={() => scrollByCard(1)}
        >
          ›
        </button>
      </div>

      {zoomItem && (
        <div className="dish-gallery-zoom-modal" role="dialog" aria-modal="true" aria-label={zoomItem.title || heading} onClick={() => setZoomItem(null)}>
          <div className="dish-gallery-zoom-card" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="dish-gallery-zoom-close" onClick={() => setZoomItem(null)} aria-label="Fermer le zoom">
              ×
            </button>
            <img src={zoomItem.imageUrl} alt={zoomItem.title || heading} />
            <p>{String(zoomItem.title || "Photo du plat").trim() || "Photo du plat"}</p>
          </div>
        </div>
      )}
    </section>
  );
}

export default DishGalleryCarousel;